import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PeptidesWiki.css';
import SEOHead from './SEOHead';

const peptidesData = {
  'glp1': {
    title: 'GLP-1 and Related Multi-agonists',
    overview: 'GLP-1 receptor agonists are a class of medications that mimic the action of glucagon-like peptide-1, a hormone that regulates blood sugar and appetite. These peptides have revolutionized the treatment of type 2 diabetes and obesity.',
    subcategories: {
      'established': {
        title: 'Established GLP-1 Receptor Agonists',
        peptides: {
          'semaglutide': {
            name: 'Semaglutide',
            brands: 'Ozempic / Wegovy',
            summary: 'Once-weekly GLP-1 agonist with exceptional efficacy for both diabetes and weight management.',
            mechanism: 'Selective GLP-1 receptor agonist with 94% amino acid homology to native GLP-1.',
            dosing: 'Start 0.25mg weekly, titrate to 0.5-2.4mg weekly depending on indication.',
            efficacy: 'Up to 15-20% body weight reduction in obesity trials (STEP program).',
            safety: 'Common: nausea, diarrhea, constipation. Rare: pancreatitis, gastroparesis.',
            status: 'FDA approved for T2DM (Ozempic) and chronic weight management (Wegovy).'
          },
          'tirzepatide': {
            name: 'Tirzepatide',
            brands: 'Mounjaro / Zepbound',
            summary: 'Dual GLP-1/GIP agonist showing superior efficacy to semaglutide in head-to-head trials.',
            mechanism: 'Dual agonist of GLP-1 and GIP (glucose-dependent insulinotropic polypeptide) receptors.',
            dosing: 'Start 2.5mg weekly, titrate up to 15mg weekly.',
            efficacy: 'Up to 22.5% weight loss at highest doses in SURMOUNT trials.',
            safety: 'Similar to other GLP-1 agonists, with potentially less nausea due to GIP activity.',
            status: 'FDA approved for T2DM (Mounjaro) and obesity (Zepbound).'
          },
          'liraglutide': {
            name: 'Liraglutide',
            brands: 'Victoza / Saxenda',
            summary: 'Daily GLP-1 agonist, one of the first to show cardiovascular benefits.',
            mechanism: 'GLP-1 receptor agonist with 97% amino acid homology to native GLP-1.',
            dosing: 'Daily injection: 0.6mg to 3mg depending on indication.',
            efficacy: '5-10% weight loss in obesity trials, significant CV risk reduction.',
            safety: 'Similar GI side effects to class, established long-term safety profile.',
            status: 'FDA approved for T2DM, obesity, and CV risk reduction.'
          },
          'dulaglutide': {
            name: 'Dulaglutide',
            brands: 'Trulicity',
            summary: 'Weekly GLP-1 agonist with convenient single-use pen.',
            mechanism: 'GLP-1 receptor agonist fused to human IgG4 Fc fragment.',
            dosing: '0.75mg to 4.5mg weekly.',
            efficacy: 'Moderate weight loss (3-5kg), strong CV outcomes data.',
            safety: 'Well-tolerated with typical class effects.',
            status: 'FDA approved for T2DM and CV risk reduction.'
          },
          'exenatide': {
            name: 'Exenatide',
            brands: 'Byetta / Bydureon',
            summary: 'First GLP-1 agonist to market, available in twice-daily and weekly formulations.',
            mechanism: 'Synthetic version of exendin-4, found in Gila monster saliva.',
            dosing: 'Byetta: 5-10mcg twice daily. Bydureon: 2mg weekly.',
            efficacy: 'Modest weight loss (2-3kg), good glycemic control.',
            safety: 'Higher rates of nausea than newer agents, antibody formation possible.',
            status: 'FDA approved for T2DM.'
          }
        }
      },
      'nextgen': {
        title: 'Next-Generation Multi-agonists',
        peptides: {
          'retatrutide': {
            name: 'Retatrutide',
            brands: 'LY3437943 (Eli Lilly)',
            summary: 'Triple agonist targeting GLP-1, GIP, and glucagon receptors for unprecedented weight loss.',
            mechanism: 'Triple receptor agonist (GLP-1/GIP/Glucagon) with balanced activity.',
            dosing: 'Weekly injection, doses up to 12mg in trials.',
            efficacy: 'Up to 24% weight loss in 48 weeks (Phase 2 data).',
            safety: 'Similar to GLP-1 class, with potential for increased heart rate from glucagon.',
            status: 'Phase 3 trials ongoing (TRIUMPH program).'
          },
          'cagrisema': {
            name: 'CagriSema',
            brands: 'Novo Nordisk',
            summary: 'Combination of semaglutide with cagrilintide (amylin analog) for enhanced weight loss.',
            mechanism: 'Dual action: GLP-1 (semaglutide) plus amylin receptor agonist (cagrilintide).',
            dosing: 'Weekly injection, 2.4mg semaglutide + 2.4mg cagrilintide.',
            efficacy: 'Targeting >25% weight loss based on early data.',
            safety: 'Under investigation, potentially additive GI effects.',
            status: 'Phase 3 trials ongoing (REDEFINE program).'
          },
          'maritide': {
            name: 'MariTide',
            brands: 'Amgen',
            summary: 'Novel antibody-peptide conjugate with extended half-life for monthly dosing.',
            mechanism: 'GLP-1 receptor agonist antibody with GIP antagonism.',
            dosing: 'Monthly or less frequent dosing being explored.',
            efficacy: 'Up to 20% weight loss in Phase 2, with continued loss at 16 weeks.',
            safety: 'Favorable tolerability profile in early studies.',
            status: 'Phase 2 complete, Phase 3 planning.'
          },
          'pemvidutide': {
            name: 'Pemvidutide',
            brands: 'Altimmune',
            summary: 'Dual GLP-1/glucagon agonist with potential metabolic advantages.',
            mechanism: 'Balanced dual agonist of GLP-1 and glucagon receptors.',
            dosing: 'Weekly subcutaneous injection.',
            efficacy: '15.6% weight loss at 48 weeks in MOMENTUM trial.',
            safety: 'Well-tolerated with typical class effects.',
            status: 'Phase 2b complete, preparing for Phase 3.'
          }
        }
      }
    }
  },
  'wellness': {
    title: 'Wellness / Anti-aging Peptides',
    overview: 'These peptides are used for healing, recovery, anti-aging, and performance enhancement. While some have extensive research, many are still considered experimental.',
    subcategories: {
      'healing': {
        title: 'Healing & Recovery',
        peptides: {
          'bpc157': {
            name: 'BPC-157',
            brands: 'Body Protection Compound-157',
            summary: 'Synthetic peptide derived from gastric juice with remarkable healing properties.',
            mechanism: 'Promotes angiogenesis, modulates growth factors, anti-inflammatory effects.',
            dosing: 'Typically 250-500mcg daily, subcutaneous or oral.',
            efficacy: 'Accelerates healing of muscles, tendons, ligaments, and gut in animal studies.',
            safety: 'Limited human data, generally well-tolerated in research settings.',
            status: 'Research compound, not FDA approved.'
          },
          'tesamorelin': {
            name: 'Tesamorelin',
            brands: 'Egrifta',
            summary: 'Growth hormone-releasing hormone analog for reducing visceral adipose tissue.',
            mechanism: 'Stimulates pituitary to release growth hormone.',
            dosing: '2mg daily subcutaneous injection.',
            efficacy: 'Reduces visceral fat by 15-20% in HIV lipodystrophy.',
            safety: 'May increase glucose, IGF-1 levels. Monitor for diabetes risk.',
            status: 'FDA approved for HIV-associated lipodystrophy.'
          }
        }
      },
      'gh': {
        title: 'Growth Hormone Secretagogues',
        peptides: {
          'cjc1295': {
            name: 'CJC-1295',
            brands: 'Modified GRF 1-29',
            summary: 'Long-acting growth hormone-releasing hormone analog.',
            mechanism: 'Stimulates pulsatile GH release from pituitary.',
            dosing: 'With DAC: 2mg weekly. Without DAC: 100-200mcg 2-3x daily.',
            efficacy: 'Increases GH and IGF-1 levels, improves body composition.',
            safety: 'Potential for water retention, carpal tunnel symptoms.',
            status: 'Research compound, not FDA approved.'
          },
          'ipamorelin': {
            name: 'Ipamorelin',
            brands: 'Research designation',
            summary: 'Selective growth hormone secretagogue with minimal side effects.',
            mechanism: 'Selective ghrelin receptor agonist without cortisol/prolactin effects.',
            dosing: '200-300mcg 2-3 times daily.',
            efficacy: 'Increases GH release without affecting other hormones.',
            safety: 'Well-tolerated with minimal side effects compared to other GHRPs.',
            status: 'Research compound, not FDA approved.'
          },
          'sermorelin': {
            name: 'Sermorelin',
            brands: 'Geref',
            summary: 'Synthetic growth hormone-releasing hormone for GH deficiency.',
            mechanism: 'GHRH analog that stimulates natural GH production.',
            dosing: '0.2-0.3mg daily before bedtime.',
            efficacy: 'Restores youthful GH patterns, improves sleep and recovery.',
            safety: 'Generally safe, may cause injection site reactions.',
            status: 'FDA approved for pediatric GH deficiency, off-label adult use.'
          }
        }
      },
      'muscle': {
        title: 'Muscle Growth Factors',
        peptides: {
          'igf1lr3': {
            name: 'IGF-1 LR3',
            brands: 'Long R3 IGF-1',
            summary: 'Modified insulin-like growth factor with extended half-life.',
            mechanism: 'Direct activation of IGF-1 receptors, reduced binding to IGF binding proteins.',
            dosing: '20-100mcg daily, typically cycled 4-6 weeks.',
            efficacy: 'Promotes muscle growth and fat loss in research settings.',
            safety: 'Risk of hypoglycemia, potential for organ growth.',
            status: 'Research compound, not FDA approved.'
          }
        }
      }
    }
  },
  'cosmetic': {
    title: 'Cosmetic / Skin Peptides',
    overview: 'Peptides used for skin rejuvenation, anti-aging, and cosmetic purposes. These range from topical formulations to injectable compounds.',
    subcategories: {
      'skin': {
        title: 'Skin Rejuvenation',
        peptides: {
          'ghkcu': {
            name: 'GHK-Cu',
            brands: 'Copper Peptide',
            summary: 'Naturally occurring copper complex with skin regenerative properties.',
            mechanism: 'Stimulates collagen production, antioxidant, anti-inflammatory.',
            dosing: 'Topical: 0.1-2% cream. Injectable: 1-2mg daily.',
            efficacy: 'Improves skin elasticity, reduces wrinkles, accelerates wound healing.',
            safety: 'Generally safe topically, limited data on injection.',
            status: 'Available in cosmetic products, not FDA approved for injection.'
          },
          'argireline': {
            name: 'Acetyl Hexapeptide-8',
            brands: 'Argireline',
            summary: 'Topical peptide marketed as "Botox in a cream" for wrinkle reduction.',
            mechanism: 'Inhibits SNARE complex formation, reducing muscle contractions.',
            dosing: 'Topical application 5-10% concentration twice daily.',
            efficacy: 'Reduces wrinkle depth by up to 30% in clinical studies.',
            safety: 'Well-tolerated topically, no systemic effects.',
            status: 'Widely used in cosmetic products.'
          }
        }
      }
    }
  }
};

function PeptidesWiki({ initialCategory = 'glp1', initialSubcategory = 'established', initialPeptide = 'semaglutide', hideSidebar = false }) {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedSubcategory, setSelectedSubcategory] = useState(initialSubcategory);
  const [selectedPeptide, setSelectedPeptide] = useState(initialPeptide);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(hideSidebar);

  // Update state when props change
  useEffect(() => {
    setSelectedCategory(initialCategory);
    setSelectedSubcategory(initialSubcategory);
    setSelectedPeptide(initialPeptide);
  }, [initialCategory, initialSubcategory, initialPeptide]);

  const currentCategory = peptidesData[selectedCategory];
  const currentSubcategory = currentCategory?.subcategories[selectedSubcategory];
  const currentPeptide = currentSubcategory?.peptides[selectedPeptide];

  return (
    <div className="wiki-container">
      {currentPeptide && (
        <SEOHead 
          title={`${currentPeptide.name} Calculator & Dosage Guide - ${currentPeptide.brands || 'Peptide Information'} | PeptideDose`}
          description={`${currentPeptide.summary} Calculate ${currentPeptide.name} dosage and reconstitution with our free peptide calculator.`}
          peptide={currentPeptide}
        />
      )}
      <div className={`wiki-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <button 
          className="sidebar-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? '☰' : '✕'}
        </button>
        
        {!sidebarCollapsed && (
          <>
            <div className="sidebar-header">
              <h2 className="sidebar-title">Peptide Encyclopedia</h2>
              <Link to="/" className="back-to-calculator">
                <span>←</span> Back to Calculator
              </Link>
            </div>
            <nav className="wiki-nav">
              {Object.entries(peptidesData).map(([catKey, category]) => (
                <div key={catKey} className="nav-category">
                  <button
                    className={`nav-category-title ${selectedCategory === catKey ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedCategory(catKey);
                      const firstSubcat = Object.keys(category.subcategories)[0];
                      setSelectedSubcategory(firstSubcat);
                      const firstPeptide = Object.keys(category.subcategories[firstSubcat].peptides)[0];
                      setSelectedPeptide(firstPeptide);
                    }}
                  >
                    {category.title}
                  </button>
                  
                  {selectedCategory === catKey && (
                    <div className="nav-subcategories">
                      {Object.entries(category.subcategories).map(([subKey, subcategory]) => (
                        <div key={subKey} className="nav-subcategory">
                          <div 
                            className={`nav-subcategory-title ${selectedSubcategory === subKey ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedSubcategory(subKey);
                              const firstPeptide = Object.keys(subcategory.peptides)[0];
                              setSelectedPeptide(firstPeptide);
                            }}
                          >
                            {subcategory.title}
                          </div>
                          
                          {selectedSubcategory === subKey && (
                            <ul className="nav-peptides">
                              {Object.entries(subcategory.peptides).map(([pepKey, peptide]) => (
                                <li key={pepKey}>
                                  <button
                                    className={`nav-peptide ${selectedPeptide === pepKey ? 'active' : ''}`}
                                    onClick={() => setSelectedPeptide(pepKey)}
                                  >
                                    {peptide.name}
                                    {peptide.brands && (
                                      <span className="nav-peptide-brand">{peptide.brands}</span>
                                    )}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </>
        )}
      </div>

      <main className="wiki-content">
        <div className="content-header">
          <div className="breadcrumb">
            <span>{currentCategory?.title}</span>
            <span className="separator">›</span>
            <span>{currentSubcategory?.title}</span>
          </div>
        </div>

        {currentPeptide && (
          <article className="peptide-article">
            <header className="article-header">
              <h1 className="peptide-name">{currentPeptide.name}</h1>
              {currentPeptide.brands && (
                <div className="peptide-brands">{currentPeptide.brands}</div>
              )}
              {currentPeptide.status && (
                <div className="peptide-status">
                  <span className={`status-badge ${currentPeptide.status.includes('FDA approved') ? 'approved' : 'research'}`}>
                    {currentPeptide.status.includes('FDA approved') ? 'FDA Approved' : 'Research'}
                  </span>
                </div>
              )}
            </header>

            <section className="article-summary">
              <h2>Summary</h2>
              <p>{currentPeptide.summary}</p>
            </section>

            <div className="article-grid">
              <section className="article-section">
                <h3>🧬 Mechanism of Action</h3>
                <p>{currentPeptide.mechanism}</p>
              </section>

              <section className="article-section">
                <h3>💉 Dosing</h3>
                <p>{currentPeptide.dosing}</p>
              </section>

              <section className="article-section">
                <h3>📊 Efficacy</h3>
                <p>{currentPeptide.efficacy}</p>
              </section>

              <section className="article-section">
                <h3>⚠️ Safety</h3>
                <p>{currentPeptide.safety}</p>
              </section>
            </div>

            <section className="article-footer">
              <div className="regulatory-status">
                <h3>Regulatory Status</h3>
                <p>{currentPeptide.status}</p>
              </div>
            </section>

            <div className="article-navigation">
              <button 
                className="nav-button prev"
                onClick={() => {
                  const peptideKeys = Object.keys(currentSubcategory.peptides);
                  const currentIndex = peptideKeys.indexOf(selectedPeptide);
                  if (currentIndex > 0) {
                    setSelectedPeptide(peptideKeys[currentIndex - 1]);
                  }
                }}
                disabled={Object.keys(currentSubcategory.peptides).indexOf(selectedPeptide) === 0}
              >
                ← Previous
              </button>
              
              <button 
                className="nav-button next"
                onClick={() => {
                  const peptideKeys = Object.keys(currentSubcategory.peptides);
                  const currentIndex = peptideKeys.indexOf(selectedPeptide);
                  if (currentIndex < peptideKeys.length - 1) {
                    setSelectedPeptide(peptideKeys[currentIndex + 1]);
                  }
                }}
                disabled={Object.keys(currentSubcategory.peptides).indexOf(selectedPeptide) === Object.keys(currentSubcategory.peptides).length - 1}
              >
                Next →
              </button>
            </div>
          </article>
        )}

        <footer className="wiki-footer">
          <p className="disclaimer">
            ⚠️ <strong>Medical Disclaimer:</strong> This information is for educational purposes only and is not medical advice. 
            Always consult with a qualified healthcare provider before using any peptides or medications.
          </p>
        </footer>
      </main>
    </div>
  );
}

export default PeptidesWiki;